import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  Sparkles,
  Trash2,
  ClipboardPaste,
} from "lucide-react";
import { toast } from "sonner";

import { http, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../context/CategoriesContext";
import { AdminNav } from "../components/AdminNav";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";

const SAMPLE = `Soccer U10 Dribbling\thttps://drive.google.com/file/d/EXAMPLE_ID_1/view?usp=sharing\tSoccer\t60-min practice plan\tyes
Flag Football Offense\thttps://drive.google.com/file/d/EXAMPLE_ID_2/view?usp=sharing\tFlag Football
Volleyball Serve Receive\thttps://drive.google.com/file/d/EXAMPLE_ID_3/view?usp=sharing\tVolleyball\tFormations & responsibilities`;

const truthy = new Set(["1", "yes", "y", "true", "t", "x", "✓"]);

function parseRows(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines.map((line, i) => {
    // Auto-detect tab vs comma vs pipe (in that order of preference)
    let parts;
    if (line.includes("\t")) parts = line.split("\t");
    else if (line.includes(" | ")) parts = line.split(/\s*\|\s*/);
    else parts = line.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/); // CSV-aware comma split
    parts = parts.map((p) => p.replace(/^"|"$/g, "").trim());

    const [title, link, folder, description, featuredRaw] = parts;
    const errors = [];
    if (!title) errors.push("Missing title");
    if (!link) errors.push("Missing Drive link");
    if (link && !/drive\.google\.com\/(file\/d\/|open\?id=|.*[?&]id=)/.test(link))
      errors.push("Doesn't look like a Google Drive link");

    return {
      lineNo: i + 1,
      title: title || "",
      googleDriveLink: link || "",
      folder: folder || "",
      description: description || "",
      featured: truthy.has((featuredRaw || "").toLowerCase()),
      errors,
    };
  });
}

export default function AdminImport() {
  const { user } = useAuth();
  const { categories, refresh: refreshCategories } = useCategories();
  const [text, setText] = useState("");
  const [defaultFolder, setDefaultFolder] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const rows = useMemo(() => parseRows(text), [text]);
  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const missingFolderRows = useMemo(
    () => validRows.filter((r) => !r.folder).length,
    [validRows]
  );

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handlePasteSample = () => {
    setText(SAMPLE);
    toast.dismiss();
    toast.success("Sample loaded — replace with your own rows");
  };

  const handleClear = () => {
    setText("");
    setResult(null);
  };

  const handleImport = async () => {
    if (validRows.length === 0) return;
    if (missingFolderRows > 0 && !defaultFolder) {
      toast.dismiss();
      toast.error(`${missingFolderRows} row(s) have no category — pick a default first`);
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await http.post("/pdfs/bulk", {
        items: validRows.map((r) => ({
          title: r.title,
          googleDriveLink: r.googleDriveLink,
          folder: r.folder || undefined,
          description: r.description,
          featured: r.featured,
        })),
        defaultFolder: defaultFolder || undefined,
      });
      setResult(data);
      await refreshCategories();
      toast.dismiss();
      const { summary } = data;
      toast.success(
        `Imported ${summary.created} · skipped ${summary.skipped}` +
          (summary.new_categories ? ` · +${summary.new_categories} new categor${summary.new_categories === 1 ? "y" : "ies"}` : "")
      );
      if (summary.created > 0) {
        // Clear textarea so the user can do another batch
        setText("");
      }
    } catch (e) {
      toast.dismiss();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <AdminNav />

      <div className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
          Bulk Import
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Paste rows from a spreadsheet (one PDF per line). Tab, comma or pipe separators all work. Missing
          categories are created automatically.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-brand-accent-soft text-brand-primary flex items-center justify-center shrink-0">
            <Info className="h-4 w-4" />
          </div>
          <div className="text-sm text-slate-600 leading-relaxed">
            <span className="font-bold text-slate-900">Format per line:</span>{" "}
            <code className="px-1.5 py-0.5 rounded bg-slate-100 text-xs font-mono text-slate-800">
              Title &lt;tab&gt; Drive Link &lt;tab&gt; Category &lt;tab&gt; Description &lt;tab&gt; Featured (yes/no)
            </code>
            <br />
            <span className="text-slate-500 text-xs">
              Only Title and Drive Link are required. Pick a default category below for any row missing one.
            </span>
          </div>
        </div>

        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Rows</Label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          spellCheck={false}
          placeholder={"Paste rows here — one PDF per line"}
          className="mt-1.5 font-mono text-sm leading-relaxed"
          data-testid="import-textarea"
        />

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 min-w-[220px]">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              Default category
            </Label>
            <Select value={defaultFolder} onValueChange={setDefaultFolder}>
              <SelectTrigger className="h-10" data-testid="import-default-folder">
                <SelectValue placeholder="None — require per row" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button
              type="button"
              variant="outline"
              onClick={handlePasteSample}
              className="h-10"
              data-testid="import-sample-btn"
            >
              <ClipboardPaste className="h-4 w-4" />
              Load sample
            </Button>
            {text && (
              <Button
                type="button"
                variant="outline"
                onClick={handleClear}
                className="h-10 text-slate-600"
                data-testid="import-clear-btn"
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Live preview */}
      {rows.length > 0 && (
        <div
          className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-6"
          data-testid="import-preview"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              Preview · {rows.length} row{rows.length === 1 ? "" : "s"}
            </div>
            <div className="text-xs flex items-center gap-3">
              <span className="text-emerald-700">{validRows.length} valid</span>
              {rows.length - validRows.length > 0 && (
                <span className="text-rose-700">{rows.length - validRows.length} with errors</span>
              )}
            </div>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50/60">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Featured</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const willAutoCreate = r.folder && !categories.find((c) => c.name === r.folder);
                  return (
                    <tr key={r.lineNo} className={cn(r.errors.length && "bg-rose-50/40")}>
                      <td className="px-3 py-2 text-slate-400 text-xs">{r.lineNo}</td>
                      <td className="px-3 py-2 font-medium text-slate-800 truncate max-w-[280px]">
                        {r.title || <span className="text-slate-400 italic">(empty)</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {r.folder ? (
                          <span className="inline-flex items-center gap-1.5">
                            {r.folder}
                            {willAutoCreate && (
                              <Badge className="rounded-full bg-brand-accent-soft text-brand-primary border-transparent text-[10px] font-bold">
                                NEW
                              </Badge>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">{defaultFolder || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.featured ? (
                          <Sparkles className="h-3.5 w-3.5 text-brand-primary" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.errors.length === 0 ? (
                          <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-semibold">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Ready
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-rose-700 text-xs font-semibold">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {r.errors[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={handleImport}
          disabled={submitting || validRows.length === 0}
          className="bg-brand-accent hover:bg-brand-accent-hover text-brand-primary font-bold rounded-lg h-11 px-5"
          data-testid="import-submit-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Import {validRows.length > 0 ? `${validRows.length} PDF${validRows.length === 1 ? "" : "s"}` : ""}
        </Button>
        {missingFolderRows > 0 && !defaultFolder && (
          <span className="text-xs text-amber-700 inline-flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            {missingFolderRows} row{missingFolderRows === 1 ? "" : "s"} need a default category
          </span>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="import-result">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Import summary</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Created" value={result.summary.created} tone="emerald" />
            <Stat label="Skipped" value={result.summary.skipped} tone="amber" />
            <Stat label="Errors" value={result.summary.errors} tone="rose" />
            <Stat label="New cats" value={result.summary.new_categories} tone="brand" />
          </div>
          {result.skipped?.length > 0 && (
            <DetailList title="Skipped (duplicate titles)" items={result.skipped.map((s) => s.title)} />
          )}
          {result.errors?.length > 0 && (
            <DetailList
              title="Errors"
              items={result.errors.map((e) => `${e.title || "(no title)"} — ${e.error}`)}
              tone="rose"
            />
          )}
          {result.new_categories?.length > 0 && (
            <DetailList
              title="Auto-created categories (color: slate by default)"
              items={result.new_categories.map((c) => c.name)}
              tone="brand"
            />
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    amber: "bg-amber-50 text-amber-900 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    brand: "bg-brand-accent-soft text-brand-primary border-brand-accent",
  };
  return (
    <div className={cn("rounded-xl border p-3", map[tone])}>
      <div className="text-xs font-bold uppercase tracking-[0.15em] opacity-80">{label}</div>
      <div className="font-display text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function DetailList({ title, items, tone = "slate" }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{title}</div>
      <ul className={cn("text-sm space-y-1", tone === "rose" && "text-rose-700", tone === "brand" && "text-brand-primary")}>
        {items.map((t, i) => (
          <li key={`${i}-${t}`} className="font-mono text-xs">
            • {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
